'use client'

import {
  INewsFields,
  IReviewFields,
  INewsEntry,
  IReviewEntry,
  IFaqEntry,
  IFaqFields,
  ContentfulListResponse,
  ContentfulAsset,
  IFaqAsset,
} from '@/types/contentful'
import { fetchContentfulData, fetchContentfulFaqData, fetchContentfulFaqDetail, fetchContentfulTermsData } from '@/utils/contentful'
import { useQuery } from '@tanstack/react-query'
import type {
  ITermsEntry,
  ITermsWithId,
  ContentfulEntry,
  ContentfulListResponseWithIncludes,
} from '@/types/contentful'

export const useReviewsDataQuery = (contentType: string) => {
  return useQuery<IReviewFields[], Error>({
    queryKey: ['reviews'],
    queryFn: async () => {
      const data = await fetchContentfulData(contentType)
      if (data) {
        return data.items.map((item: IReviewEntry) => item.fields)
      } else {
        throw new Error('Failed to fetch reviews')
      }
    }
  })
}

export const useNewsDataQuery = (contentType: string) => {
  return useQuery<INewsFields[], Error>({
    queryKey: ['news'],
    queryFn: async () => {
      const data = await fetchContentfulData(contentType)
      if (data) {
        return data.items.map((item: INewsEntry) => item.fields)
      } else {
        throw new Error('Failed to fetch news')
      }
    }
  })
}


export const useFaqDataQuery = (contentType: string, category: string) => {
  return useQuery<{ faqs: IFaqFields[] }, Error>({
    queryKey: ['faq', contentType, category],
    queryFn: async () => {
      const data = (await fetchContentfulFaqData(contentType, category)) as ContentfulListResponse<IFaqEntry>
      if (!data) throw new Error('Failed to fetch faq')

      const faqs: IFaqFields[] = data.items.map((item: IFaqEntry) => ({ ...item.fields, id: item.sys.id }))
      return { faqs }
    }
  })
}

export const useFaqDetailDataQuery = (id: string) => {
  return useQuery<{ faq: IFaqFields, assets: IFaqAsset[] }, Error>({
    queryKey: ['faq', id],
    queryFn: async () => {
      const data = await fetchContentfulFaqDetail(id)
      if (!data) throw new Error('Failed to fetch faq detail')

      const faq = data.items[0].fields
      const assets = data.includes?.Asset?.map((asset: ContentfulAsset) => ({
        id: asset.sys.id,
        title: asset.fields.title ?? '',
        url: asset.fields.file?.url ?? ''
      })) ?? []


      return { faq, assets }
    }
  })
}

export const useTermsDataQuery = (contentType: string, category: string) => {
  return useQuery<{ terms: ITermsWithId[]; entriesById: Record<string, ContentfulEntry> }, Error>({
    queryKey: ['terms', category],
    queryFn: async () => {
      const data = (await fetchContentfulTermsData(contentType, category)) as ContentfulListResponseWithIncludes<ITermsEntry>
      if (!data) throw new Error('Failed to fetch terms')

      const terms: ITermsWithId[] = data.items.map((item) => ({ id: item.sys.id, ...item.fields }))

      const entriesById = (data.includes?.Entry ?? []).reduce((acc: Record<string, ContentfulEntry>, cur) => {
        acc[cur.sys.id] = cur
        return acc
      }, {})
      return { terms, entriesById }
    }
  })
}